import { useState, useEffect, useRef } from 'react';
import { Download, Copy, Check } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Switch } from '@/components/ui/Switch';
import { copyToClipboard, downloadCanvas } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

interface FontStyle {
  id: string;
  family: string;
  label: string;
  weight: number;
  /** Google Fonts URL — loaded lazily on first render */
  url: string;
}

const FONTS: FontStyle[] = [
  { id: 'caveat', family: 'Caveat', label: 'Casual handwriting', weight: 600, url: 'Caveat:wght@400;600;700' },
  { id: 'dancing', family: 'Dancing Script', label: 'Flowy script', weight: 700, url: 'Dancing+Script:wght@400;600;700' },
  { id: 'allura', family: 'Allura', label: 'Elegant copperplate', weight: 400, url: 'Allura' },
  { id: 'great-vibes', family: 'Great Vibes', label: 'Wedding-style', weight: 400, url: 'Great+Vibes' },
  { id: 'sacramento', family: 'Sacramento', label: 'Single-stroke', weight: 400, url: 'Sacramento' },
  { id: 'pacifico', family: 'Pacifico', label: 'Bold loopy', weight: 400, url: 'Pacifico' },
  { id: 'kaushan', family: 'Kaushan Script', label: 'Brush-like', weight: 400, url: 'Kaushan+Script' },
  { id: 'satisfy', family: 'Satisfy', label: 'Friendly hand', weight: 400, url: 'Satisfy' },
  { id: 'shadows', family: 'Shadows Into Light Two', label: 'Quick scribble', weight: 400, url: 'Shadows+Into+Light+Two' },
  { id: 'homemade', family: 'Homemade Apple', label: 'Pen & paper', weight: 400, url: 'Homemade+Apple' },
];

let fontsInjected = false;
function injectFontStylesheet() {
  if (fontsInjected || typeof document === 'undefined') return;
  fontsInjected = true;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${FONTS.map((f) => `family=${f.url}`).join('&')}&display=swap`;
  document.head.appendChild(link);
}

export function TextToSignature() {
  const [name, setName] = useState('Aarav Sharma');
  const [fontIdx, setFontIdx] = useState(0);
  const [size, setSize] = useState(72);
  const [color, setColor] = useState('#0a0a0b');
  const [italic, setItalic] = useState(false);
  const [transparent, setTransparent] = useState(true);
  const [bg, setBg] = useState('#ffffff');
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    injectFontStylesheet();
  }, []);

  // Render whenever inputs change
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const font = FONTS[fontIdx];
    const text = name || ' ';

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d')!;
    const fontSpec = `${italic ? 'italic ' : ''}${font.weight} ${size}px "${font.family}", cursive`;
    measureCtx.font = fontSpec;
    const m = measureCtx.measureText(text);
    const padX = Math.max(20, size * 0.4);
    const padY = Math.max(16, size * 0.35);
    const w = Math.ceil(m.width + padX * 2);
    const h = Math.ceil(size * 1.5 + padY * 2);

    const dpr = window.devicePixelRatio || 1;
    c.width = w * dpr;
    c.height = h * dpr;
    c.style.width = `${w}px`;
    c.style.height = `${h}px`;
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    if (!transparent) {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
    }
    ctx.font = fontSpec;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.fillText(text, padX, h / 2);
  }, [name, fontIdx, size, color, italic, transparent, bg]);

  function exportPng() {
    if (!canvasRef.current) return;
    downloadCanvas(canvasRef.current, `signature-${name.replace(/\s+/g, '-').toLowerCase() || 'signature'}.png`);
    toast.success('Signature saved');
  }

  async function exportDataUrl() {
    if (!canvasRef.current) return;
    const url = canvasRef.current.toDataURL('image/png');
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Data URL copied');
    }
  }

  function exportSvg() {
    const font = FONTS[fontIdx];
    const text = name || ' ';
    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d')!;
    const fontSpec = `${italic ? 'italic ' : ''}${font.weight} ${size}px "${font.family}", cursive`;
    measureCtx.font = fontSpec;
    const w = Math.ceil(measureCtx.measureText(text).width + 60);
    const h = Math.ceil(size * 1.6);
    const svg =
      `<?xml version="1.0" encoding="UTF-8"?>` +
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">` +
      `<style>@import url('https://fonts.googleapis.com/css2?family=${font.url}&amp;display=swap');</style>` +
      (transparent ? '' : `<rect width="${w}" height="${h}" fill="${bg}"/>`) +
      `<text x="30" y="${h / 2}" dominant-baseline="middle" font-family="'${font.family}', cursive" font-size="${size}" font-weight="${font.weight}"${italic ? ' font-style="italic"' : ''} fill="${color}">${escapeXml(text)}</text>` +
      `</svg>`;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `signature-${name.replace(/\s+/g, '-').toLowerCase() || 'signature'}.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('SVG saved');
  }

  return (
    <div>
      <ToolHeader
        toolId="text-to-signature"
        title="Text to signature"
        description="Type a name and turn it into a styled handwritten signature. Export as transparent PNG or SVG."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Name to sign">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type your full name…"
            />
          </Field>
          <Field label="Color">
            <div className="grid grid-cols-6 gap-1.5">
              {['#0a0a0b', '#1d4ed8', '#0e7490', '#15803d', '#a16207', '#b91c1c'].map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={cn(
                    'h-8 rounded-md border-2 transition',
                    color === c ? 'border-accent' : 'border-fg/85 dark:border-fg/40',
                  )}
                  style={{ background: c }}
                />
              ))}
            </div>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="!w-full !h-9 mt-2"
            />
          </Field>
          <Field label="Size" hint={`${size}px`}>
            <Slider value={size} onChange={setSize} min={32} max={140} step={2} showValue={false} />
          </Field>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="text-fg-muted">Slanted (italic)</span>
            <Switch checked={italic} onChange={setItalic} />
          </label>
          <label className="flex items-center justify-between text-sm cursor-pointer">
            <span className="text-fg-muted">Transparent background</span>
            <Switch checked={transparent} onChange={setTransparent} />
          </label>
          {!transparent && (
            <Field label="Background">
              <input
                type="color"
                value={bg}
                onChange={(e) => setBg(e.target.value)}
                className="!w-full !h-9"
              />
            </Field>
          )}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border">
            <Button size="sm" variant="ghost" onClick={exportSvg}>
              <Download size={11} />
              SVG
            </Button>
            <Button size="sm" variant="ghost" onClick={exportDataUrl}>
              {copied ? <Check size={11} /> : <Copy size={11} />}
              URL
            </Button>
            <Button size="sm" variant="primary" onClick={exportPng}>
              <Download size={11} />
              PNG
            </Button>
          </div>
        </Card>

        <div className="lg:col-span-2 lg:order-1 space-y-3">
          <Card className="!p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Preview · {FONTS[fontIdx].label}
            </div>
            <div
              className="p-8 flex items-center justify-center min-h-[200px]"
              style={{
                background: transparent
                  ? 'repeating-conic-gradient(hsl(var(--bg-inset)) 0% 25%, hsl(var(--bg-elev)) 25% 50%) 50% / 16px 16px'
                  : bg,
              }}
            >
              <canvas ref={canvasRef} className="max-w-full" />
            </div>
          </Card>

          <Card className="!p-0 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
              Choose a style
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {FONTS.map((f, i) => (
                <button
                  key={f.id}
                  onClick={() => setFontIdx(i)}
                  className={cn(
                    'flex flex-col items-center justify-center px-4 py-4 transition group',
                    fontIdx === i ? 'bg-accent/10' : 'hover:bg-surface-hover',
                  )}
                >
                  <span
                    className="text-3xl"
                    style={{
                      fontFamily: `"${f.family}", cursive`,
                      fontWeight: f.weight,
                      color: fontIdx === i ? color : 'hsl(var(--fg))',
                    }}
                  >
                    {name || 'Your name'}
                  </span>
                  <span className="text-2xs text-fg-subtle mt-1 uppercase tracking-wider">
                    {f.label}
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function escapeXml(s: string): string {
  return s.replace(/[<>&"']/g, (c) => ({
    '<': '&lt;',
    '>': '&gt;',
    '&': '&amp;',
    '"': '&quot;',
    "'": '&apos;',
  })[c]!);
}

export default TextToSignature;
